import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import DatePicker from '../../../../../../../../common/data-entry/DatePicker';
import Input from '../../../../../../../../common/data-entry/Input';
import Select from '../../../../../../../../common/data-entry/Select';
import Toggle from '../../../../../../../../common/data-entry/Toggle';
import { i18nt } from '../../../../../../../../root';
import { isTodayOrBefore } from '../../../../../../../../services/date/validators';
import { NS_COMMON, NSC_PATIENT, NSPP_PATIENT } from '../../../../../../../../services/i18n/namespaces';

const otherPrimaryLanguageId = 'OT';

export default function PatientDetails({
  isEditMode,
  handleDateOfBirthChange,
  handleInputChange,
  handleSexChange,
  handlePronounsChange,
  handlePrimaryLanguageChange,
  isRequiredField,
  getValidationMessage,
  medicalRecordNumber,
  setMedicalRecordNumber,
  info,
  fieldsDataDictionary
}) {
  const { t } = useTranslation();
  const { firstName, lastName, middleName, preferredName, dob, isDeceased, sex, pronouns, primaryLanguage } = info;

  const primaryLanguages = useMemo(() => {
    if (!fieldsDataDictionary?.languageCodes) {
      return [];
    }
    const sortedLanguages = fieldsDataDictionary.languageCodes
      .filter(languageCode => languageCode.id !== otherPrimaryLanguageId)
      .map(languageCode => ({
        id: languageCode.id,
        name: t(`languageMap.${languageCode.id}`, { ns: NSC_PATIENT })
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    sortedLanguages.push({
      id: otherPrimaryLanguageId,
      name: t(`languageMap.${otherPrimaryLanguageId}`, { ns: NSC_PATIENT })
    });
    return sortedLanguages;
  }, [fieldsDataDictionary?.languageCodes, t]);

  return (
    <div className="row">
      <div className="col-12 apm-form-label">{t('addOrEdit.patientDetails', { ns: NSPP_PATIENT })}</div>
      <div className="col-4">
        <Input
          label={t('addOrEdit.firstName', { ns: NSPP_PATIENT })}
          name="firstName"
          value={firstName}
          onChange={handleInputChange}
          required={isRequiredField('firstName')}
          validationMessage={getValidationMessage('firstName')}
        />
      </div>
      <div className="col-4">
        <Input
          label={t('addOrEdit.lastName', { ns: NSPP_PATIENT })}
          name="lastName"
          value={lastName}
          onChange={handleInputChange}
          required={isRequiredField('lastName')}
          validationMessage={getValidationMessage('lastName')}
        />
      </div>
      <div className="col-4">
        <Input
          label={t('addOrEdit.middleName', { ns: NSPP_PATIENT })}
          name="middleName"
          value={middleName}
          onChange={handleInputChange}
        />
      </div>
      <div className="col-4">
        <Input
          label={t('preferredName', { ns: NSPP_PATIENT })}
          name="preferredName"
          value={preferredName}
          onChange={handleInputChange}
        />
      </div>
      <div className="col-4">
        <Select
          label={t('sex', { ns: NSC_PATIENT })}
          name="sex"
          value={fieldsDataDictionary?.sexCodes?.find(({ id }) => id === sex) || null}
          onChange={handleSexChange}
          dataSource={fieldsDataDictionary?.sexCodes}
          clearSearchOnSelection
          optionValueKey="name"
          customOptionTemplateFunction={sexCustomOptionTemplateFunction}
          customSelectedValueTemplateFunction={sexCustomSelectedValueTemplateFunction}
        />
      </div>
      <div className="col-4">
        <Select
          label={t('pronouns', { ns: NSPP_PATIENT })}
          name="pronouns"
          value={fieldsDataDictionary?.pronounsCodes?.find(({ id }) => id === pronouns) || null}
          onChange={handlePronounsChange}
          deselectOnSelectedOptionClick={false}
          dataSource={fieldsDataDictionary?.pronounsCodes}
          optionValueKey="name"
          customOptionTemplateFunction={pronounsCustomOptionTemplateFunction}
          customSelectedValueTemplateFunction={pronounsCustomSelectedValueTemplateFunction}
        />
      </div>

      <div className="col-4">
        <DatePicker.Manual
          label={t('dateOfBirth', { ns: NS_COMMON })}
          value={moment.isMoment(dob) && dob.format('DD/MMM/YYYY')}
          required={isRequiredField('dob')}
          isValidDate={isTodayOrBefore}
          dateOnly={true}
          onChange={d => handleDateOfBirthChange(d, 'dob')}
          validationMessage={getValidationMessage('dob')}
        />
      </div>
      {!isEditMode && (
        <div className="col-4">
          <Input
            label={t('addOrEdit.medicalRecordNumber', { ns: NSPP_PATIENT })}
            name="medicalRecordNumber"
            value={medicalRecordNumber}
            onChange={e => setMedicalRecordNumber(e.target.value)}
            validationMessage={getValidationMessage('medicalRecordNumber')}
            required={isRequiredField('medicalRecordNumber')}
          />
        </div>
      )}
      <div className="col-4">
        <Select
          label={t('primaryLanguage', { ns: NSPP_PATIENT })}
          name="primaryLanguage"
          value={primaryLanguages.find(({ id }) => id === primaryLanguage)}
          onChange={handlePrimaryLanguageChange}
          deselectOnSelectedOptionClick={false}
          dataSource={primaryLanguages}
          optionValueKey="name"
          searchable={true}
        />
      </div>

      <div className="col-12" />

      <div className="col-4 mb-4">
        <Toggle name="isDeceased" checked={isDeceased} onChange={handleInputChange}>
          {t('deceased', { ns: NSC_PATIENT })}
        </Toggle>
      </div>
    </div>
  );
}

function sexCustomOptionTemplateFunction(option) {
  return i18nt(`sexMap.${option.id}`, { ns: NSC_PATIENT });
}

function sexCustomSelectedValueTemplateFunction(options) {
  if (!options[0]) {
    return null;
  }
  return sexCustomOptionTemplateFunction(options[0]);
}

function pronounsCustomOptionTemplateFunction(option) {
  return i18nt(`pronounMap.${option.id}`, { ns: NSC_PATIENT });
}

function pronounsCustomSelectedValueTemplateFunction(options) {
  if (!options[0]) {
    return null;
  }
  return pronounsCustomOptionTemplateFunction(options[0]);
}
